import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export interface QBOConnectionStatus {
  is_connected: boolean;
  realm_id?: string;
  company_name?: string;
  connected_by?: string;
  connected_at?: string;
  last_refreshed_at?: string;
}

export interface QBOSyncStatus {
  is_synced: boolean;
  sync_status: 'synced' | 'error' | 'stale' | 'not_synced';
  qbo_id?: string;
  last_synced_at?: string;
  sync_error?: string;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useQuickBooksStatus() {
  return useQuery({
    queryKey: ['quickbooks', 'status'],
    queryFn: async () => {
      const response = await api.quickbooks.getStatus();
      return response as QBOConnectionStatus & { success: boolean };
    },
  });
}

export function useInvoiceSyncStatus(invoiceId: string, enabled = true) {
  return useQuery({
    queryKey: ['quickbooks', 'sync-status', invoiceId],
    queryFn: async () => {
      const response = await api.quickbooks.getSyncStatus(invoiceId);
      return response as QBOSyncStatus & { success: boolean };
    },
    enabled,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useQuickBooksAuthorize() {
  return useMutation({
    mutationFn: () => api.quickbooks.authorize(),
    onError: (error: any) => {
      toast.error(error.message || 'Failed to start QuickBooks authorization');
    },
  });
}

export function useQuickBooksCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { code: string; state: string; realm_id: string }) =>
      api.quickbooks.callback(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks'] });
      toast.success(result.message || 'QuickBooks connected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to connect QuickBooks');
    },
  });
}

export function useQuickBooksDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.quickbooks.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks'] });
      toast.success('QuickBooks disconnected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to disconnect QuickBooks');
    },
  });
}

export function useSyncInvoiceToQBO() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.quickbooks.syncInvoice(invoiceId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks', 'sync-status'] });
      toast.success(result.message || 'Invoice synced to QuickBooks');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to sync invoice to QuickBooks');
    },
  });
}
